import { logger } from '@/lib/logger';
import { toErrorInstance } from '@/lib/utils';

export const TRANSCRIPT_KEY_PREFIX = 'transcript:';
const SEGMENT_SEPARATOR = ':';
const INTRO_TRANSCRIPT_KEY_PREFIX = 'biography-intro-transcript';

function assertNoSeparator(userId: string): void {
  if (userId.includes(SEGMENT_SEPARATOR)) {
    throw new Error(
      `userId must not contain ${SEGMENT_SEPARATOR} — would break sweep ownership check (got: ${userId})`
    );
  }
}

export function buildTranscriptKey(userId: string, collectionId: string): string {
  assertNoSeparator(userId);
  return `${TRANSCRIPT_KEY_PREFIX}${userId}${SEGMENT_SEPARATOR}${collectionId}`;
}

/**
 * Sweep stale transcript-shaped localStorage keys. Catches three cases in one pass:
 *   1. Other-user transcript keys (the cross-user leak we originally fixed)
 *   2. Legacy un-scoped transcript keys written before user-scoping was introduced
 *   3. All `biography-intro-transcript*` keys (defunct after legacy chat removal — no readers,
 *      covers both the colon-scoped form `biography-intro-transcript:*` and the legacy hyphen form `biography-intro-transcript-*`)
 *
 * Idempotent: safe to call repeatedly. Client-only — no-op on the server.
 */
export function sweepForeignTranscriptKeys(currentUserId: string): void {
  if (typeof window === 'undefined') return;
  assertNoSeparator(currentUserId);

  const ownTranscriptPrefix = `${TRANSCRIPT_KEY_PREFIX}${currentUserId}${SEGMENT_SEPARATOR}`;

  // Phase 1: enumerate. Storage.length and Storage.key are spec-stable; do not
  // wrap in try/catch — if enumeration fails we want the error surfaced.
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key === null) continue;

    const isTranscript = key.startsWith(TRANSCRIPT_KEY_PREFIX);
    const isIntro = key.startsWith(INTRO_TRANSCRIPT_KEY_PREFIX);

    if (!isTranscript && !isIntro) continue;

    // Intros are always defunct; transcripts only when foreign.
    if (isIntro || (isTranscript && !key.startsWith(ownTranscriptPrefix))) toRemove.push(key);
  }

  // Phase 2: remove. Per-key try so one quota/security error doesn't abort the rest.
  let removed = 0;
  for (const key of toRemove) {
    try {
      localStorage.removeItem(key);
      removed += 1;
    } catch (error) {
      logger.warn(
        '[transcript-keys] removeItem failed',
        { key },
        { error: toErrorInstance(error) }
      );
    }
  }

  if (removed > 0) {
    logger.info('[transcript-keys] swept stale transcript keys', { count: removed });
  }
}
